/**
 * Desktop info for Sentry correlation between showpass-electron and showpass-nextjs.
 */
export interface DesktopInfo {
  desktopVersion: string;
  desktopSessionId: string;
}

/**
 * ElectronView interface exposed by the desktop app's preload script.
 * Provides detection of Electron environment and access to native features.
 */
export interface ElectronViewAPI {
  /** Whether the app is running inside Electron */
  isElectron: boolean;
  /** The platform the desktop app is running on (darwin, win32, linux) */
  platform: string;
  /**
   * Get the desktop app version.
   * Returns null if not yet received from main process.
   */
  getDesktopVersion?: () => string | null;
  /**
   * Get the desktop session ID for Sentry correlation.
   * Returns null if not yet received from main process.
   */
  getDesktopSessionId?: () => string | null;
  /**
   * Register a callback to be notified when desktop info is ready.
   * If the info is already available, the callback is invoked immediately.
   * @param callback - Function to call with desktopVersion and desktopSessionId
   */
  onDesktopInfoReady?: (callback: (info: DesktopInfo) => void) => void;
  /**
   * Get the current WebSocket agent port.
   * Returns null if the port hasn't been received yet from the main process.
   */
  getAgentPort?: () => number | null;
  /**
   * Register a callback to be notified when the agent port is ready.
   * If the port is already available, the callback is invoked immediately.
   * @param callback - Function to call with the port number
   */
  onAgentPortReady?: (callback: (port: number) => void) => void;
  /**
   * Open a URL in the OS default browser (Chrome, Safari, etc.)
   * instead of opening it in the Electron window.
   * Used for "Save as PDF" to let users use the native print dialog.
   * @param url - The URL to open
   * @returns Promise with success/error result
   */
  openExternal?: (url: string) => Promise<{ success: boolean; error?: string }>;
}

declare global {
  interface Window {
    ElectronView?: ElectronViewAPI;
  }
}

/**
 * Get the agent port from Electron's preload script if available.
 * Returns null if not running in Electron or port not yet received.
 */
export const getElectronAgentPort = (): number | null => {
  if (typeof window === "undefined") return null;

  const electronView = window.ElectronView;
  if (
    electronView?.isElectron &&
    typeof electronView.getAgentPort === "function"
  ) {
    return electronView.getAgentPort();
  }

  return null;
};

/**
 * Register a callback to be notified when the Electron agent port is ready.
 * Does nothing if not running in Electron.
 * @param callback - Function to call with the port number
 */
export const onElectronAgentPortReady = (
  callback: (port: number) => void
): void => {
  if (typeof window === "undefined") return;

  const electronView = window.ElectronView;
  if (
    electronView?.isElectron &&
    typeof electronView.onAgentPortReady === "function"
  ) {
    electronView.onAgentPortReady(callback);
  }
};

export interface OpenBrowserResult {
  success: boolean;
  error?: string;
}

/**
 * Opens a URL in the OS default browser.
 *
 * On desktop (Electron): Uses shell.openExternal via IPC to open in Chrome/Safari
 * On web: Falls back to window.open which opens in a new tab
 *
 * This is the preferred way to open PDFs for "Save as PDF" functionality,
 * allowing users to use the OS native print dialog.
 *
 * @param url - The URL to open
 * @returns Result indicating success or failure with error message
 */
export const openInOsBrowser = async (
  url: string
): Promise<OpenBrowserResult> => {
  if (typeof window === "undefined") {
    return { success: false, error: "Cannot open browser during SSR" };
  }

  const electronView = window.ElectronView;

  // On desktop, use Electron's shell.openExternal via IPC
  if (
    electronView?.isElectron &&
    typeof electronView.openExternal === "function"
  ) {
    const result = await electronView.openExternal(url);
    return result;
  }

  // On web, fall back to window.open
  const newWindow = window.open(url, "_blank");
  if (!newWindow) {
    return {
      success: false,
      error: "Failed to open browser window. Please check popup blocker.",
    };
  }

  return { success: true };
};
